import { apiFetch } from "@/lib/api";

export type CashFlowGranularity = "week" | "month";
export type CashFlowDirection = "inflow" | "outflow";
export type CashFlowSourceKind = "open_po" | "gipi" | "open_so" | "gosi" | "cbp_monthly_statement";

export interface CashFlowForecastLine {
  direction: CashFlowDirection;
  source_kind: CashFlowSourceKind;
  document_ref: string;
  document_id: string | null;
  counterparty: string | null;
  currency: string;
  amount: number;
  anchor_date: string | null;
  payment_terms_label: string | null;
  due_date: string | null;
  location_code: string | null;
  detail: string | null;
  link_path: string | null;
  purchase_order_mv_id: number | null;
  purchase_invoice_mv_id: number | null;
}

export interface CashFlowUnknownExpense extends CashFlowForecastLine {
  reasons: string[];
}

export interface CashFlowCurrencyBucketTotals {
  inflow: number;
  outflow: number;
  net: number;
}

export interface CashFlowForecastBucket {
  period_start: string;
  period_end: string;
  by_currency: Record<string, CashFlowCurrencyBucketTotals>;
}

export interface CashFlowForecastTotals {
  inflow: number;
  outflow: number;
  net: number;
  unknown_outflow: number;
}

export interface CashFlowApAgingFilterSummary {
  as_of_date: string | null;
  imported_at: string;
  filename: string;
  open_gipi_tsh_count: number;
}

export interface CashFlowArAgingFilterSummary {
  as_of_date: string | null;
  imported_at: string;
  filename: string;
  open_gosi_count: number;
}

export interface CashFlowForecastResult {
  from: string;
  to: string;
  granularity: CashFlowGranularity;
  display_currency: "USD";
  buckets: CashFlowForecastBucket[];
  lines: CashFlowForecastLine[];
  unknown_expenses: CashFlowUnknownExpense[];
  totals: CashFlowForecastTotals;
  ap_aging_filter: CashFlowApAgingFilterSummary | null;
  ar_aging_filter: CashFlowArAgingFilterSummary | null;
}

export interface CnyUsdExchangeRateRow {
  date: string;
  cny_per_usd: number;
  source: string;
}

export interface CnyUsdExchangeRateResult {
  from: string;
  to: string;
  pair: "CNY/USD";
  unit: "cny_per_usd";
  description: string;
  rows: CnyUsdExchangeRateRow[];
}

export interface CashFlowForecastQuery {
  from?: string;
  to?: string;
  granularity?: CashFlowGranularity;
}

export async function fetchCashFlowForecast(
  query: CashFlowForecastQuery,
): Promise<CashFlowForecastResult> {
  const params = new URLSearchParams();
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  if (query.granularity) params.set("granularity", query.granularity);
  const suffix = params.toString() === "" ? "" : `?${params.toString()}`;
  return apiFetch<CashFlowForecastResult>(`/api/accounting/cash-flow-forecast${suffix}`);
}

export async function fetchCnyUsdExchangeRates(
  from: string,
  to: string,
): Promise<CnyUsdExchangeRateResult> {
  const params = new URLSearchParams({ from, to });
  return apiFetch<CnyUsdExchangeRateResult>(`/api/accounting/cny-usd-exchange-rates?${params.toString()}`);
}

export const CASH_FLOW_SOURCE_KIND_LABEL: Record<CashFlowSourceKind, string> = {
  open_po: "Open purchase order",
  gipi: "GIPI / TSH",
  cbp_monthly_statement: "CBP monthly statement",
  open_so: "Open sales order",
  gosi: "GOSI",
};

export function firstOfUtcMonth(date: Date): string {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
}

export function addUtcMonthsIso(monthStartIso: string, months: number): string {
  const parts = monthStartIso.split("-").map((part) => Number.parseInt(part, 10));
  const year = parts[0] ?? 2026;
  const month = parts[1] ?? 1;
  const next = new Date(Date.UTC(year, month - 1 + months, 1));
  return next.toISOString().slice(0, 10);
}
